import { isValidElement } from 'react';

import type {
  EmptyStateAction,
  EmptyStateProps,
  EmptyStateSize,
  EmptyStateVariant,
  Presentation,
} from './EmptyState.types';
import type { ReactNode } from 'react';

import { Button, LinkButton } from '@/components/ui/Button';

/**
 * Presentation × size → the treatment.
 *
 * The two presentations keep their own scales on purpose. A framed empty state
 * sits on the page surface and reads as a slot; a bare one sits inside a card
 * that already carries a title, so it is set larger and airier to hold the card
 * on its own. Flattening both onto one scale would restyle one of them, which
 * is a design decision rather than a de-duplication.
 */
const PRESENTATION: Record<
  EmptyStateVariant,
  Record<EmptyStateSize, Presentation>
> = {
  framed: {
    compact: {
      container:
        'rounded-xl border border-dashed border-stroke-default bg-lift-1 px-3 py-4 text-center',
      title: 'text-body-md',
      description: 'text-body-sm',
      descriptionColor: 'text-neutral-500',
      actionWrapper: 'mt-3',
      actionSize: 'sm',
    },
    default: {
      container:
        'rounded-xl border border-dashed border-stroke-default bg-lift-1 px-4 py-6 text-center',
      title: 'text-body-lg',
      description: 'text-body-md',
      descriptionColor: 'text-neutral-500',
      actionWrapper: 'mt-3',
      actionSize: 'md',
    },
  },
  bare: {
    compact: {
      container:
        'flex flex-col items-center justify-center px-4 py-6 text-center',
      title: 'text-body-lg',
      description: 'mt-1 max-w-sm text-body-md',
      descriptionColor: 'text-neutral-400',
      actionWrapper: 'mt-4',
      actionSize: 'sm',
    },
    default: {
      container:
        'flex flex-col items-center justify-center px-6 py-12 text-center',
      title: 'text-body-xl',
      description: 'mt-2 max-w-sm text-body-md',
      descriptionColor: 'text-neutral-400',
      actionWrapper: 'mt-6',
      actionSize: 'md',
    },
  },
};

/**
 * Distinguishes the `{ label, href?, onClick? }` convenience descriptor from an
 * arbitrary node. An element, an array and a primitive are all nodes; only a
 * plain object carrying `label` is the descriptor.
 */
function isActionDescriptor(
  action: ReactNode | EmptyStateAction
): action is EmptyStateAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    !isValidElement(action) &&
    !Array.isArray(action) &&
    'label' in action
  );
}

/**
 * EmptyState - the "nothing here yet" block.
 *
 * One component for both presentations the app uses. There were two — this one
 * and a second in `dashboard/shared/` — and both rendered
 * `data-testid="empty-state"`, so a test that queried it could silently match
 * the wrong component.
 *
 * @example
 * // Framed: a slot on the page surface, action as a node
 * <EmptyState
 *   title="No workouts yet"
 *   description="Add one when you're ready."
 *   action={<Button variant="primary">Add workout</Button>}
 * />
 *
 * // Bare: inside a card, action as a descriptor
 * <EmptyState
 *   variant="bare"
 *   title="No workouts yet"
 *   description="Complete your first workout to start tracking progress."
 *   action={{ label: 'Start a workout', href: '/workouts' }}
 * />
 */
export function EmptyState({
  title,
  description,
  action,
  size = 'compact',
  variant = 'framed',
  className = '',
  ...props
}: EmptyStateProps) {
  const styles = PRESENTATION[variant][size];

  const actionNode =
    action && isActionDescriptor(action) ? (
      action.href ? (
        <LinkButton
          to={action.href}
          variant={action.variant ?? 'contrast'}
          size={styles.actionSize}
          data-testid="empty-state-action"
        >
          {action.label}
        </LinkButton>
      ) : (
        <Button
          variant={action.variant ?? 'contrast'}
          size={styles.actionSize}
          onClick={action.onClick}
          data-testid="empty-state-action"
        >
          {action.label}
        </Button>
      )
    ) : (
      action
    );

  return (
    <div
      data-testid="empty-state"
      data-variant={variant}
      data-size={size}
      className={`${styles.container} ${className}`.trim()}
      {...props}
    >
      <p
        className={`${styles.title} mb-1 font-semibold text-neutral-50`}
        data-testid="empty-state-title"
      >
        {title}
      </p>

      {description && (
        <p
          className={`${styles.description} ${styles.descriptionColor}`}
          data-testid="empty-state-description"
        >
          {description}
        </p>
      )}

      {actionNode ? (
        <div className={styles.actionWrapper}>{actionNode}</div>
      ) : null}
    </div>
  );
}

export default EmptyState;
